import { graphql } from 'gatsby';
import React from 'react';
import Seo from '../../components/SEO';
import ArticleBody from '../../components/ArticleBody/ArticleBody';
import ArticleCover from '../../components/ArticleCover/ArticleCover';
import ArticleListing from '../../components/ArticleListing/ArticleListing';
import ArticleNav from '../../components/ArticleNav/ArticleNav';
import HeaderTheme from '../../components/Header/HeaderTheme';
import Promotions from '../../components/Promotions/Promotions';
import { Grid, GridItem } from '../../components/UI/Grid/Grid';
import Section, { themes } from '../../components/UI/Section/Section';
import Type from '../../components/UI/Type/Type';
import usePromotions from '../../hooks/usePromotions';

const PostTemplate = ({ 
  data: { page, relatedPosts, prevPage, nextPage },
  location: { pathname }
}) => {
  const {
    title,
    uri,
    article,
    author,
    categories,
    tags,
    metadata,
    seo,
    relatedServices: { services },
    sectionPromotions,
  } = page;
  const promotions = usePromotions(sectionPromotions.promotions, services, {
    excludePath: pathname
  });

  return (
    <>
      <HeaderTheme />
      
      <Seo
        title={title}
        description={article.excerpt || metadata.metaDescription}
        image={metadata.metaImage || article.image}
        seo={seo}
      />
      
      <ArticleNav />
      <ArticleCover
        teaser={categories.nodes[ 0 ]?.name}
        heading={title}
        image={article.image}
        author={author.node}
      />
      <ArticleBody
        body={article.body}
        share={{ url: uri, subject: title }}
        author={author.node}
        tags={tags.nodes}
        services={services}
        prevPage={prevPage}
        nextPage={nextPage}
      />

      <Promotions items={promotions} />

      {!!relatedPosts.nodes.length && (
        <Section theme={themes.DEFAULT}>
          <Grid justify="center">
            <GridItem md={10} xl={6}>
              <Type.MetaHeading as="h2">Related articles</Type.MetaHeading>
              <ArticleListing nodes={relatedPosts.nodes} isList />
            </GridItem>
          </Grid>
        </Section>
      )}
    </>
  );
};

export default PostTemplate;

export const query = graphql`
  query post($id: String!, $nextPage: String, $previousPage: String, $tagIds: [String]) {
    page: wpPost(id: {eq: $id}) {
      ...Post
      nodeType
      title
      uri
      seo {
          title
          metaDesc
          focuskw
          metaKeywords
          metaRobotsNoindex
          metaRobotsNofollow
          opengraphTitle
          opengraphDescription
          opengraphImage {
              altText
              sourceUrl
              srcSet
          }
          twitterTitle
          twitterDescription
          twitterImage {
              altText
              sourceUrl
              srcSet
          }
          canonical
          cornerstone
          schema {
              articleType
              pageType
              raw
          }
      }
      metadata {
        metaDescription
        metaImage {
          ...WpImage
        }
      }
      author {
        node {
          description
          userAvatar {
            avatar {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 160
                    height: 160
                    transformOptions: {grayscale: true}
                    layout: FIXED
                  )
                }
              }
            }
          }
        }
      }
      article {
        body {
          ... on WpPost_Article_Body_LayoutWysiwyg {
            fieldGroupName
            wysiwyg
          }
          ... on WpPost_Article_Body_LayoutImage {
            fieldGroupName
            image {
              title
              ...WpImage
            }
          }
          ... on WpPost_Article_Body_LayoutVideo {
            fieldGroupName
            caption
            video
          }
        }
      }
      tags {
        nodes {
          id
          name
          uri
        }
      }
      relatedServices {
        services {
          ...ServiceCard
        }
      }
      sectionPromotions {
        promotions {
          promotion {
            ...Promotion
          }
        }
      }
    }
    relatedPosts: allWpPost(
      filter: {id: {ne: $id}, tags: {nodes: {elemMatch: {id: {in: $tagIds}}}}}
      sort: {modifiedGmt: DESC}
      limit: 3
    ) {
      nodes {
        ...Post
      }
    }
    nextPage: wpPost(id: {eq: $nextPage}) {
      title
      uri
    }
    prevPage: wpPost(id: {eq: $previousPage}) {
      title
      uri
    }
  }
`;
